/** Shared by the partner's lead page and the Sales Manager's review page: conversation, timeline and documents. */
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { Badge, Spinner, ErrorState } from '../../components/common/index.jsx';
import { ButtonSpinner } from '../../components/common/Loading.jsx';
import { pushToast } from '../ui/uiSlice.js';
import { dateTime, fileSize } from '../../utils/formatters.js';
import * as api from '../../api/partnerPortal.api.js';
import { errorText } from './partnerShared.jsx';

/** Comments (with replies) merged with status events, oldest first. `internal` = the Sales Manager / rep view. */
export function Conversation({ leadId, internal = false, locked = false }) {
  const qc = useQueryClient();
  const dispatch = useDispatch();
  const key = ['partner', 'conversation', leadId];
  const q = useQuery({ queryKey: key, queryFn: async () => ({ comments: await api.comments(leadId), events: await api.timeline(leadId) }) });
  const [text, setText] = useState('');
  const [visibility, setVisibility] = useState('partner_visible');
  const [replyTo, setReplyTo] = useState(null);

  const send = useMutation({
    mutationFn: () => {
      const body = { comment_text: text.trim(), ...(internal ? { visibility } : {}) };
      return replyTo ? api.replyTo(leadId, replyTo.id, body) : api.addComment(leadId, body);
    },
    onSuccess: () => { setText(''); setReplyTo(null); qc.invalidateQueries({ queryKey: key }); },
    onError: (e) => dispatch(pushToast({ tone: 'danger', message: 'Could not post the comment', detail: errorText(e) })),
  });

  if (q.isLoading) return <Spinner />;
  if (q.isError) return <ErrorState error={q.error} onRetry={q.refetch} />;
  const { comments, events } = q.data;
  const byId = Object.fromEntries(comments.map((c) => [c.id, c]));
  const items = [
    ...events.filter((e) => e.type === 'event').map((e) => ({ kind: 'event', id: e.id, at: e.at, title: e.title, text: e.text, author: e.author, visibility: e.visibility })),
    ...comments.map((c) => ({ kind: 'comment', ...c, at: c.created_at })),
  ].sort((a, b) => new Date(a.at) - new Date(b.at));

  return (
    <div>
      <div className="d-flex flex-column gap-3 mb-3">
        {items.length === 0 && <div className="t-sm text-muted">No conversation yet. Start one below.</div>}
        {items.map((it) => (it.kind === 'event' ? (
          <div key={`e${it.id}`} className="d-flex gap-2 t-sm align-items-start">
            <span className="dot-ind mt-2" style={{ background: 'var(--brand-500)' }} />
            <div><span className="fw-600">{it.title}</span>{it.author && <span className="text-muted"> · {it.author}</span>}
              {it.text && <div className="t-xs">{it.text}</div>}<div className="t-2xs text-muted">{dateTime(it.at)}</div></div>
          </div>
        ) : (
          <div key={it.id} className="p-3 rounded" style={{ background: it.visibility === 'internal_only' ? 'var(--amber-50, #fff7e6)' : 'var(--surface-2)', border: '1px solid var(--line-2)', marginLeft: it.parent_comment_id ? 24 : 0 }}>
            <div className="d-flex justify-content-between align-items-start">
              <div className="t-sm"><span className="fw-650">{it.author_name}</span> <Badge tone="neutral">{it.author_role}</Badge>
                {it.visibility === 'internal_only' && <Badge tone="amber" className="ms-1">Internal only</Badge>}</div>
              <div className="t-2xs text-muted">{dateTime(it.at)}</div>
            </div>
            {it.parent_comment_id && byId[it.parent_comment_id] && <div className="t-2xs text-muted mt-1">↳ replying to {byId[it.parent_comment_id].author_name}</div>}
            <div className="t-sm mt-1" style={{ whiteSpace: 'pre-wrap' }}>{it.comment_text}</div>
            {!locked && <button type="button" className="btn btn-link btn-sm p-0 mt-1" onClick={() => setReplyTo(it)}>Reply</button>}
          </div>
        )))}
      </div>

      {locked ? <div className="t-sm text-muted">This conversation is closed because the lead was rejected.</div> : (
        <div>
          {replyTo && <div className="t-xs mb-1">Replying to <b>{replyTo.author_name}</b> <button type="button" className="btn btn-link btn-sm p-0 ms-1" onClick={() => setReplyTo(null)}>cancel</button></div>}
          <textarea className="form-control mb-2" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder={replyTo ? 'Write a reply…' : 'Write a comment…'} />
          <div className="d-flex align-items-center gap-2">
            {internal && (
              <select className="form-select form-select-sm w-auto" value={visibility} onChange={(e) => setVisibility(e.target.value)}>
                <option value="partner_visible">Visible to partner</option><option value="internal_only">Internal note (hidden from partner)</option>
              </select>
            )}
            <button type="button" className="btn btn-save btn-sm-x ms-auto" disabled={!text.trim() || send.isPending} onClick={() => send.mutate()}>{send.isPending ? <><ButtonSpinner />Posting…</> : 'Post'}</button>
          </div>
          <div className="t-2xs text-muted mt-1">Comments can't be edited or deleted — post a new message to correct something.</div>
        </div>
      )}
    </div>
  );
}

export function Documents({ leadId, internal = false, locked = false }) {
  const qc = useQueryClient();
  const dispatch = useDispatch();
  const fileRef = useRef(null);
  const [visibility, setVisibility] = useState('partner_visible');
  const key = ['partner', 'documents', leadId];
  const q = useQuery({ queryKey: key, queryFn: () => api.documents(leadId) });
  const up = useMutation({
    mutationFn: async (files) => { for (const f of files) await api.uploadDocument(leadId, f, internal ? visibility : undefined); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); dispatch(pushToast({ tone: 'success', message: 'Uploaded' })); },
    onError: (e) => dispatch(pushToast({ tone: 'danger', message: 'Upload failed', detail: errorText(e) })),
  });
  const open = async (d) => {
    try { const r = await api.documentDownloadUrl(leadId, d.id); window.open(r.url, '_blank', 'noopener'); } catch (e) { dispatch(pushToast({ tone: 'danger', message: 'Could not open the file', detail: errorText(e) })); }
  };
  if (q.isLoading) return <Spinner />;
  if (q.isError) return <ErrorState error={q.error} onRetry={q.refetch} />;
  return (
    <div>
      {!locked && (
        <div className="d-flex gap-2 align-items-center mb-3">
          <input ref={fileRef} type="file" multiple hidden onChange={(e) => { const fl = [...e.target.files]; e.target.value = ''; if (fl.length) up.mutate(fl); }} />
          <button type="button" className="btn btn-edit btn-sm-x" disabled={up.isPending} onClick={() => fileRef.current?.click()}>{up.isPending ? <><ButtonSpinner />Uploading…</> : <><i className="bi bi-upload me-1" aria-hidden="true" />Upload files</>}</button>
          {internal && <select className="form-select form-select-sm w-auto" value={visibility} onChange={(e) => setVisibility(e.target.value)}><option value="partner_visible">Visible to partner</option><option value="internal_only">Internal only</option></select>}
        </div>
      )}
      {q.data.length === 0 ? <div className="t-sm text-muted">No documents.</div> : (
        <div className="table-x-wrap"><table className="table-x is-dense"><thead><tr><th>File</th><th>Size</th><th>Uploaded by</th><th>On</th><th /></tr></thead>
          <tbody>{q.data.map((d) => (
            <tr key={d.id}><td className="fw-600"><i className="bi bi-file-earmark me-1" aria-hidden="true" />{d.file_name}{d.visibility === 'internal_only' && <Badge tone="amber" className="ms-1">Internal</Badge>}</td>
              <td>{fileSize(Number(d.file_size))}</td><td>{d.uploaded_by}</td><td>{dateTime(d.created_at)}</td>
              <td className="text-end"><button type="button" className="btn btn-edit btn-sm-x" onClick={() => open(d)}>Download</button></td></tr>
          ))}</tbody></table></div>
      )}
    </div>
  );
}
